import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Star, MapPin, Clock, ShieldCheck, Check } from "lucide-react";

export default function ParkingDetail() {
  const { offerId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const airport = params.get("airport");
  const dropoff = params.get("dropoff");
  const pickup = params.get("pickup");
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/offers/${encodeURIComponent(offerId)}`, { params: { airport_code: airport, dropoff, pickup } })
      .then((r) => setOffer(r.data))
      .catch(() => setOffer(null))
      .finally(() => setLoading(false));
  }, [offerId, airport, dropoff, pickup]);

  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!offer) return <div className="p-10">
    <div className="font-display text-2xl">Offer not available</div>
    <p className="text-slate-500 mt-2">Please run the search again.</p>
    <Link to="/" className="btn-navy inline-block mt-4 rounded-full px-5 py-2.5 text-sm">Back to search</Link>
  </div>;

  const days = Math.max(1, Math.ceil((new Date(pickup) - new Date(dropoff)) / 86400000));
  const goCheckout = () => {
    const q = new URLSearchParams({
      offer_id: offer.offer_id, canonical_parking_id: offer.canonical_parking_id, airport_code: airport,
      dropoff, pickup, total_price: offer.total_price, name: offer.name, category: offer.category, provider_id: offer.provider_id,
    }).toString();
    nav(`/checkout?${q}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <img src={offer.image_url} alt={offer.name} className="w-full h-80 object-cover rounded-2xl" data-testid="detail-hero-image" />
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold tracking-widest bg-slate-900 text-white rounded-md px-2 py-1">{(offer.category || "").replace("_", " ").toUpperCase()}</span>
            {offer.source_type === "community" && <span className="text-[10px] font-bold tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-md px-2 py-1 inline-flex items-center gap-1"><ShieldCheck size={10}/> APPROVED SPACE</span>}
          </div>
          <h1 className="font-display text-4xl font-semibold mt-4 text-[var(--fpn-navy)]" data-testid="detail-title">{offer.name}</h1>
          <div className="mt-3 text-sm text-slate-600 flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><MapPin size={14}/> {offer.distance_miles} mi from airport</span>
            {offer.transfer_minutes ? <span className="inline-flex items-center gap-1"><Clock size={14}/> {offer.transfer_minutes} min transfer</span> : null}
            <span className="inline-flex items-center gap-1"><Star size={14} className="text-amber-500" fill="currentColor"/> {offer.rating} ({offer.review_count} reviews)</span>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-[var(--fpn-navy)]">Features</h2>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {(offer.features || []).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-700"><Check size={14} className="text-[var(--fpn-amber-2)]"/> {f}</div>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-[var(--fpn-navy)]">Cancellation</h2>
            <p className="text-sm text-slate-600 mt-2">{offer.cancellation_type === "flexible" ? "Free cancellation up to 24 hours before drop-off." : offer.cancellation_type === "moderate" ? "Free cancellation up to 72 hours before drop-off." : "Non-refundable."}</p>
          </div>

          {offer.source_type === "community" && (
            <div className="mt-8 border border-emerald-200 bg-emerald-50 rounded-2xl p-5 text-sm text-emerald-900">
              <strong>Privacy note:</strong> exact address is revealed only after a confirmed booking. Approximate location shown above.
            </div>
          )}
        </div>

        {/* Booking summary */}
        <aside>
          <div className="border border-slate-200 rounded-2xl p-6 bg-white sticky top-24" data-testid="booking-summary">
            <div className="text-xs text-slate-500">Total for {days} day{days > 1 ? "s" : ""}</div>
            <div className="font-display text-4xl font-bold text-[var(--fpn-navy)]">£{offer.total_price.toFixed(2)}</div>
            <div className="text-xs text-slate-500">£{(offer.total_price / days).toFixed(2)}/day</div>

            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Parking</span><span>£{offer.parking_price.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Service fee</span><span>£{(offer.service_fee || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Discount</span><span>−£{(offer.discount || 0).toFixed(2)}</span></div>
              <div className="border-t border-slate-100 pt-2 flex justify-between font-semibold"><span>Total</span><span>£{offer.total_price.toFixed(2)}</span></div>
            </div>

            <button onClick={goCheckout} className="btn-amber w-full rounded-full mt-6 py-3 text-sm" data-testid="book-parking-button">Book Parking</button>
            <p className="text-[11px] text-slate-500 mt-3">Payment processed securely by Stripe. Apple Pay and Google Pay supported.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
